import type { GridFilterCellProps } from "@tql/ui-components-react/kendo/grid";
import type { InputChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import { useEffect, useRef, useState } from "react";

// Mirror Kendo's accepted shape for a filter descriptor's `operator`:
// either a built-in name (e.g. "contains", "eq") or a custom predicate.
// Use the looser `Function` here (matching Kendo's own typing) so this
// component remains assignable to `ComponentType<GridFilterCellProps>`.
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- match Kendo's `GridFilterCellProps.operator` typing so this component stays assignable to `ComponentType<GridFilterCellProps>`
type FilterOperator = string | Function;

type DebouncedTextFilterCellProps = GridFilterCellProps & {
	/**
	 * Optional custom Kendo filter operator. When provided, used instead of
	 * the default `"contains"` (case-insensitive substring match on the raw
	 * field value). Use this when the displayed/searchable representation
	 * differs from the stored value (e.g. 24-hour `HH:mm` displayed as
	 * 12-hour `h:mm a`).
	 */
	operator?: FilterOperator;
	/** Optional extra class appended to the outer `.k-filtercell` element. */
	className?: string;
};

/**
 * Text filter cell that holds the input value in local state and only
 * propagates changes up to Kendo's filter descriptor after a short debounce.
 *
 * Why: the default Kendo text filter cell is fully controlled by the grid's
 * `filter` prop, so every keystroke updates parent state and re-renders the
 * entire grid (including all custom cells). On grids with heavy custom cells
 * — MultiSelect, TimePicker, DropDownList — this makes typing visibly laggy.
 *
 * Using local state isolates the per-keystroke re-render to this single
 * input. The grid is only notified after the user pauses typing.
 */
export const DebouncedTextFilterCell = (
	props: DebouncedTextFilterCellProps,
) => {
	const externalValue = (props.value ?? "") as string;
	const [localValue, setLocalValue] = useState(externalValue);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Keep the latest onChange in a ref so the debounced call always uses
	// the current one without us having to re-create the timer on every render.
	const onChangeRef = useRef(props.onChange);
	useEffect(() => {
		onChangeRef.current = props.onChange;
	}, [props.onChange]);

	// Sync local state when the external filter is reset/changed from outside
	// (e.g. clearing all filters). Cancel any pending debounce so a stale
	// keystroke doesn't reapply the old value over the new external one.
	useEffect(() => {
		if (localValue === externalValue) return;
		if (timeoutRef.current !== null) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		setLocalValue(externalValue);
		// `localValue` intentionally omitted: this effect only reacts to
		// external changes; including it would cause it to fire on every
		// keystroke and immediately overwrite the user's input.
		// eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
	}, [externalValue]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
		};
	}, []);

	const operator = props.operator ?? "contains";
	const handleChange = (event: InputChangeEvent) => {
		const next = String(event.value ?? "");
		setLocalValue(next);
		if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
		timeoutRef.current = setTimeout(() => {
			timeoutRef.current = null;
			onChangeRef.current({
				value: next,
				operator: next === "" ? "" : operator,
				syntheticEvent: event.syntheticEvent,
			});
		}, 250);
	};

	return (
		// Mirror the wrapper structure produced by Kendo's default
		// `GridFilterCell` so the TraxGrid search-icon injector and filter-tab
		// navigation (both target `.k-filtercell`) keep working.
		<div
			className={
				props.className ? `k-filtercell ${props.className}` : "k-filtercell"
			}
		>
			<div className={"k-filtercell-wrapper"}>
				<Input
					aria-label={"Filter"}
					onChange={handleChange}
					value={localValue}
				/>
			</div>
		</div>
	);
};
